// Batch-11 F3 — instelbare bestandsnaam-patronen per documenttype.
//
// Eén gedeelde bron voor de documenttypes, de beschikbare tokens en de
// ISO-defaults, plus het Zod-schema dat de backoffice-UI én de API gebruiken.
// De daadwerkelijke token-substitutie + sanitizing leeft in de pure
// `apps/api/src/lib/filename.ts` (geen DB/deps), maar de defaults staan hier
// zodat client en server dezelfde waarheid delen.

import { z } from 'zod'

export const BESTANDSNAAM_DOCUMENT_TYPES = [
  'WERKBON',
  'FACTUUR',
  'EXPORT',
  // batch-37 schadeformulier (FO-326) — de gegenereerde aanrijdingsformulier-PDF.
  'SCHADEFORMULIER',
] as const
export type BestandsnaamDocumentType = (typeof BESTANDSNAAM_DOCUMENT_TYPES)[number]

export const BESTANDSNAAM_DOCUMENT_TYPE_LABELS: Record<BestandsnaamDocumentType, string> = {
  WERKBON: 'Werkbon-PDF',
  FACTUUR: 'Factuur-PDF',
  EXPORT: 'Export (Excel/ZIP)',
  SCHADEFORMULIER: 'Schadeformulier-PDF (aanrijdingsformulier)',
}

// De ondersteunde tokens. Niet elk token is voor elk documenttype zinvol; de UI
// toont per type welke tokens gevuld worden. Onbekende/leeg-gebleven tokens
// vallen weg in de filename (geen lege segmenten).
export const BESTANDSNAAM_TOKENS = [
  'werkbonnummer',
  'factuurnummer',
  'klantnaam',
  'weeknummer',
  'medewerkernaam',
  'periode',
  'datum',
  'type',
  // FO-303-P2 — de aanmaak-/exportdatum (`yyyy-MM-dd`, Europe/Amsterdam) als
  // laatste deel. I.t.t. `datum` (`yyyyMMdd`) mét koppeltekens.
  'aanmaakdatum',
  // batch-37 schadeformulier (FO-326) — kenteken van het betrokken voertuig.
  'kenteken',
] as const
export type BestandsnaamToken = (typeof BESTANDSNAAM_TOKENS)[number]

export const BESTANDSNAAM_TOKEN_LABELS: Record<BestandsnaamToken, string> = {
  werkbonnummer: 'Werkbonnummer',
  factuurnummer: 'Factuurnummer',
  klantnaam: 'Klantnaam',
  weeknummer: 'Weeknummer (ISO, bv. 2026-W25)',
  medewerkernaam: 'Medewerkernaam',
  periode: 'Periode',
  datum: 'Datum (sorteerbaar, bv. 20260625)',
  type: 'Type',
  aanmaakdatum: 'Aanmaakdatum (bv. 2026-07-09)',
  kenteken: 'Kenteken (bv. XX-99-XX)',
}

// Welke tokens per documenttype gevuld worden (voor de UI-hints + validatie).
export const BESTANDSNAAM_TOKENS_PER_TYPE: Record<BestandsnaamDocumentType, BestandsnaamToken[]> = {
  WERKBON: ['werkbonnummer', 'klantnaam', 'weeknummer', 'medewerkernaam', 'datum', 'aanmaakdatum'],
  FACTUUR: ['factuurnummer', 'klantnaam', 'weeknummer', 'periode', 'datum', 'aanmaakdatum'],
  EXPORT: ['type', 'klantnaam', 'medewerkernaam', 'periode', 'datum', 'aanmaakdatum'],
  // `type` = "Aanrijdingsformulier" (literale tekst valt uit patronen weg;
  // de vaste prefix komt dus als token-waarde mee). `datum` = aanrijdingsdatum.
  SCHADEFORMULIER: ['type', 'kenteken', 'medewerkernaam', 'datum', 'aanmaakdatum'],
}

// FO-075 — de app-brede vorm `<type>_<scope>_<periode>_<datum>` met `_` als
// separator (de werkbon-/factuur-namen schuiven mee naar underscores). Worden
// gebruikt zodra er geen per-type-patroon is ingesteld.
// FO-303-P2 — `[aanmaakdatum]` sluit elk default-patroon af (de exportdatum,
// `yyyy-MM-dd`); voor EXPORT vervangt hij het oude `[datum]`-slot (dat voor
// exports al de aanmaakdatum was).
export const BESTANDSNAAM_DEFAULT_PATRONEN: Record<BestandsnaamDocumentType, string> = {
  WERKBON: '[werkbonnummer]_[klantnaam]_[weeknummer]_[medewerkernaam]_[aanmaakdatum]',
  FACTUUR: '[factuurnummer]_[klantnaam]_[weeknummer]_[aanmaakdatum]',
  EXPORT: '[type]_[klantnaam]_[periode]_[aanmaakdatum]',
  // FO-326 §7: `Aanrijdingsformulier_[kenteken]_[datum]`.
  SCHADEFORMULIER: '[type]_[kenteken]_[datum]',
}

export interface BestandsnaamPatroonDTO {
  documentType: BestandsnaamDocumentType
  patroon: string
  /** True wanneer dit de ingebouwde default is (geen opgeslagen rij). */
  isDefault: boolean
}

// Patroon mag alleen letters/cijfers/spaties/koppeltekens/punten en
// `[token]`-placeholders bevatten - geen Windows-onveilige tekens. Minstens één
// token zodat de naam onderscheidend blijft.
const PATROON_REGEX = /^[\w .\-[\]]+$/u

// FO-075 — periode-achtige tokens behouden hun interne `-`/`_` (bv. `2026-W25`,
// `2026-06-01_2026-06-15`); overige tokens worden als kaal segment geslugificeerd.
const PERIODE_LIKE_TOKENS = new Set<string>(['periode', 'weeknummer', 'datum', 'aanmaakdatum'])

function previewStripDiacritics(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '')
}

/** Lokale spiegel van `sanitizeSegment` (`@streep/utils`) - types mag utils niet importeren. */
function previewSanitizeSegment(raw: string): string {
  return previewStripDiacritics(raw.trim())
    .replace(/[^A-Za-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
}

/** Lokale spiegel van `sanitizePeriode` (`@streep/utils`). */
function previewSanitizePeriode(raw: string): string {
  return previewStripDiacritics(raw.trim())
    .replace(/[^A-Za-z0-9_-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-_]+|[-_]+$/g, '')
}

/**
 * Pure preview-helper voor de UI: pas een patroon toe op voorbeeld-token-waarden
 * en lever de resulterende bestandsnaam. Spiegelt de server-side
 * `applyFilenamePattern` (lib/filename.ts) sinds FO-075: alleen de `[token]`-
 * volgorde telt, elk token wordt als segment gesanitized en de segmenten worden
 * met `_` verbonden (de app-brede vorm). Bedoeld voor een live voorbeeld.
 */
export function applyFilenamePatternPreview(
  pattern: string,
  tokens: Record<string, string | null | undefined>,
  ext: string,
): string {
  const lookup = new Map<string, string>()
  for (const [k, v] of Object.entries(tokens)) {
    const val = (v ?? '').trim()
    if (val.length > 0) lookup.set(k.toLowerCase(), val)
  }
  const segments: string[] = []
  const re = /\[([a-zA-Z]+)\]/g
  let m: RegExpExecArray | null
  while ((m = re.exec(pattern)) !== null) {
    const name = m[1]!.toLowerCase()
    const raw = lookup.get(name)
    if (!raw) continue
    const seg = PERIODE_LIKE_TOKENS.has(name)
      ? previewSanitizePeriode(raw)
      : previewSanitizeSegment(raw)
    if (seg.length > 0) segments.push(seg)
  }
  const base = segments.join('_') || 'download'
  const cleanExt = ext.replace(/^\.+/, '').trim() || 'bin'
  return `${base}.${cleanExt}`
}

export const UpdateBestandsnaamPatroonSchema = z.object({
  documentType: z.enum(BESTANDSNAAM_DOCUMENT_TYPES),
  patroon: z
    .string()
    .trim()
    .min(1, 'Geef een patroon op')
    .max(200, 'Houd het patroon onder de 200 tekens')
    .regex(
      PATROON_REGEX,
      'Alleen letters, cijfers, spaties, koppeltekens, punten en [tokens] zijn toegestaan',
    )
    .refine((p) => /\[[a-z]+\]/.test(p), 'Gebruik minstens één [token]'),
})
export type UpdateBestandsnaamPatroonInput = z.infer<typeof UpdateBestandsnaamPatroonSchema>
